import { createShortcut, Icon, Page, PageModuleNav, PageModuleNavItem } from '@app/ui';
import { array, features } from '@app/base';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useModuleVisibility, useRoleBasedAccess } from '@app/main/utils';

function useNav() {
  const { isSchedulingVisible } = useModuleVisibility();
  const { t } = useTranslation();
  const roleAccessMap = useRoleBasedAccess();

  return useMemo<PageModuleNav>(() => {
    const items: Array<PageModuleNavItem | false> = [
      roleAccessMap.OrganizationManagementRoles && {
        href: '/administration/organization-settings',
        icon: <Icon type="home" />,
        label: t('organizations.settings.title'),
        shortcut: createShortcut('shift+o'),
      },
      roleAccessMap.EmployeeManagementRoles && {
        href: '/administration/employee',
        icon: <Icon type="users" />,
        label: t('employees.title'),
        shortcut: createShortcut('shift+e'),
      },
      features.isEnabled('ActivityPage') &&
        roleAccessMap.ActivityAccessRoles && {
          href: '/administration/activity',
          icon: <Icon type="clipboard" />,
          label: t('activity.title'),
          shortcut: createShortcut('shift+a'),
        },
      roleAccessMap.TemplateManagementRoles && {
        href: 'templates',
        icon: <Icon type="snippets" />,
        label: t('nav.templates'),
        children: array.withoutDisabled([
          isSchedulingVisible && {
            href: '/administration/appointment-type-template',
            icon: <Icon type="schedule" />,
            label: t('appointmentTypeTemplates.title'),
            shortcut: createShortcut('shift+t'),
          },
          {
            href: '/administration/encounter-template',
            icon: <Icon type="file" />,
            label: t('encounterTemplates.title'),
            shortcut: createShortcut('shift+n'),
          },
          {
            href: '/administration/consent-template',
            icon: <Icon type="book" />,
            label: t('consents.templates.title'),
            shortcut: createShortcut('shift+c'),
          },
          features.isEnabled('DynamicForms') && {
            href: '/administration/dynamic-form-template/add',
            icon: <Icon type="book" />,
            label: 'Dynamic Forms',
            shortcut: createShortcut('shift+f'),
          },
        ]),
      },
      roleAccessMap.LookupManagementRoles && {
        href: 'catalogs',
        icon: <Icon type="book" />,
        label: t('nav.catalogs'),
        children: array.withoutDisabled([
          {
            href: '/administration/specialty',
            icon: <Icon type="layout" />,
            label: t('options.specialty.title'),
            shortcut: createShortcut('shift+y'),
          },
          {
            href: '/administration/care-home',
            icon: <Icon type="location" />,
            label: t('catalogCareHomes.title'),
            shortcut: createShortcut('shift+x'),
          },
        ]),
      },
      roleAccessMap.TemplateManagementRoles && {
        href: '/administration/document-tag',
        icon: <Icon type="tags" />,
        label: t('documents.tags.title'),
        shortcut: createShortcut('shift+g'),
      },
    ];

    return {
      defaultExpanded: 'admin',
      expandedWidth: 250,
      items: array.withoutDisabled(items),
    };
  }, [
    isSchedulingVisible,
    roleAccessMap.ActivityAccessRoles,
    roleAccessMap.EmployeeManagementRoles,
    roleAccessMap.LookupManagementRoles,
    roleAccessMap.OrganizationManagementRoles,
    roleAccessMap.TemplateManagementRoles,
    t,
  ]);
}

export function AdministrationPageModule() {
  const nav = useNav();

  return (
    <Page.Module nav={nav}>
      <Outlet />
    </Page.Module>
  );
}
